import { useState, useEffect, useRef } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useBranding } from '@/contexts/BrandingContext'
import {
  User,
  Mail,
  Phone,
  Building,
  MapPin,
  CheckCircle,
  XCircle,
  Edit,
  Save,
  Palette,
  Image,
  DollarSign,
  Languages,
  Sun,
  Moon,
  Search,
  ChevronDown,
  Star,
  Loader2,
  Eye,
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { helpersService } from '@/services/helpersService'
import { Country } from '@/types'
import { BrandingPreview } from '@/components/branding/BrandingPreview'
import { createBrandingFromUser, isValidColor, generateColorVariants } from '@/utils/branding'
import { getPageStyles, getCardStyles, getButtonStyles, getInputStyles, getStatusColors } from '@/utils/pageBranding'

export default function Profile() {
  const { user, updateProfile } = useAuthStore()
  const { branding, updateBranding, resetBranding, isCustomBranding } = useBranding()
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(false)
  const [showBrandingPreview, setShowBrandingPreview] = useState(false)

  // Country selection state
  const [countries, setCountries] = useState<Country[]>([])
  const [popularCountries, setPopularCountries] = useState<Country[]>([])
  const [countrySearchQuery, setCountrySearchQuery] = useState('')
  const [showCountryDropdown, setShowCountryDropdown] = useState(false)
  const [countryLoading, setCountryLoading] = useState(false)
  const countryDropdownRef = useRef<HTMLDivElement>(null)

  // File upload state
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false)
  const [uploadingBrandImage, setUploadingBrandImage] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    company_name: user?.company_name || '',
    phone_number: user?.phone_number || '',
    address: user?.address || '',
    country: user?.country || '',
    // Branding fields
    currency: user?.currency || 'USD',
    language: user?.language || 'en',
    theme: user?.theme || 'dark',

    // Primary Brand Colors
    brand_color: user?.brand_color || '#FF9902',
    brand_color_light: user?.brand_color_light || '',
    brand_color_dark: user?.brand_color_dark || '',

    // Secondary Brand Colors
    secondary_color: user?.secondary_color || '#2E3192',
    secondary_color_light: user?.secondary_color_light || '',
    secondary_color_dark: user?.secondary_color_dark || '',

    // Brand Assets
    author_image_url: user?.author_image_url || '',
    image_url: user?.image_url || '',
    name: user?.name || 'PEXIBLOCK',

    // UI Customization
    border_radius: user?.border_radius || '0.75rem',
    font_family: user?.font_family || '"Alan Sans", sans-serif',

    // Branding Settings
    use_custom_branding: user?.use_custom_branding || false,
  })

  // Get branding styles
  const currentBranding = createBrandingFromUser(formData)
  const pageStyles = getPageStyles(currentBranding)
  const cardStyles = getCardStyles(currentBranding)
  const inputStyles = getInputStyles(currentBranding)
  const statusColors = getStatusColors(currentBranding)

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        company_name: user.company_name || '',
        phone_number: user.phone_number || '',
        address: user.address || '',
        country: user.country || '',
        // Branding fields
        currency: user.currency || 'USD',
        language: user.language || 'en',
        theme: user.theme || 'dark',

        // Primary Brand Colors
        brand_color: user.brand_color || '#FF9902',
        brand_color_light: user.brand_color_light || '',
        brand_color_dark: user.brand_color_dark || '',

        // Secondary Brand Colors
        secondary_color: user.secondary_color || '#2E3192',
        secondary_color_light: user.secondary_color_light || '',
        secondary_color_dark: user.secondary_color_dark || '',

        // Brand Assets
        author_image_url: user.author_image_url || '',
        image_url: user.image_url || '',
        name: user.name || 'PEXIBLOCK',

        // UI Customization
        border_radius: user.border_radius || '0.75rem',
        font_family: user.font_family || '"Alan Sans", sans-serif',

        // Branding Settings
        use_custom_branding: user.use_custom_branding || false,
      })

      // Set country search query if user has a country
      if (user.country_name) {
        setCountrySearchQuery(`${user.country_name} (${user.country_iso_code})`)
      }
    }
  }, [user])

  useEffect(() => {
    fetchCountries()
    fetchPopularCountries()
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCountryDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Country-related functions
  const fetchCountries = async () => {
    try {
      const data = await helpersService.getCountries()
      setCountries(data)
    } catch (error) {
      console.error('Failed to fetch countries:', error)
    }
  }

  const fetchPopularCountries = async () => {
    try {
      const data = await helpersService.getPopularCountries()
      setPopularCountries(data)
    } catch (error) {
      console.error('Failed to fetch popular countries:', error)
    }
  }

  const searchCountries = async (query: string) => {
    if (!query.trim()) {
      setCountries([])
      return
    }

    setCountryLoading(true)
    try {
      const data = await helpersService.searchCountries(query)
      setCountries(data)
    } catch (error) {
      console.error('Failed to search countries:', error)
    } finally {
      setCountryLoading(false)
    }
  }

  const handleCountrySearch = (query: string) => {
    setCountrySearchQuery(query)
    if (query.length >= 2) {
      searchCountries(query)
    } else if (query.length === 0) {
      fetchCountries()
    }
  }

  const selectCountry = (country: Country) => {
    setFormData(prev => ({
      ...prev,
      country: country.id,
    }))
    setCountrySearchQuery(`${country.name} (${country.iso_code})`)
    setShowCountryDropdown(false)
  }

  // File upload functions
  const handleFileUpload = async (file: File, type: 'profile' | 'brand') => {
    if (!file) return

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Please select a valid image file (JPEG, PNG, GIF, or WebP)')
      return
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      setUploadError('File size must be less than 10MB')
      return
    }

    setUploadError(null)

    if (type === 'profile') {
      setUploadingProfileImage(true)
    } else {
      setUploadingBrandImage(true)
    }

    try {
      const uploadedFile = await helpersService.uploadDocument({
        file,
        documentType: 'image',
        purpose: type === 'profile' ? 'profile' : 'logo',
      })

      // Update form data with the new URL
      setFormData(prev => ({
        ...prev,
        [type === 'profile' ? 'author_image_url' : 'image_url']: uploadedFile.url,
      }))

      // Auto-save the profile update
      const profileData = {
        ...formData,
        [type === 'profile' ? 'author_image_url' : 'image_url']: uploadedFile.url,
        country:
          typeof formData.country === 'string'
            ? parseInt(formData.country) || undefined
            : formData.country,
      }
      await updateProfile(profileData)
    } catch (error) {
      console.error('File upload failed:', error)
      setUploadError(helpersService.handleError(error))
    } finally {
      if (type === 'profile') {
        setUploadingProfileImage(false)
      } else {
        setUploadingBrandImage(false)
      }
    }
  }

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'profile' | 'brand'
  ) => {
    const file = event.target.files?.[0]
    if (file) {
      handleFileUpload(file, type)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))

    // Auto-generate color variants for primary and secondary colors
    if (field === 'brand_color' && isValidColor(value)) {
      const variants = generateColorVariants(value)
      setFormData(prev => ({
        ...prev,
        brand_color: value,
        brand_color_light: variants.light,
        brand_color_dark: variants.dark,
      }))
    } else if (field === 'secondary_color' && isValidColor(value)) {
      const variants = generateColorVariants(value)
      setFormData(prev => ({
        ...prev,
        secondary_color: value,
        secondary_color_light: variants.light,
        secondary_color_dark: variants.dark,
      }))
    }
  }

  const handleBrandingChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))

    // Update branding context immediately for preview
    const updatedBranding = {
      ...branding,
      [field]: value,
    }
    updateBranding(updatedBranding)
  }

  const handleResetBranding = () => {
    resetBranding()
    setFormData(prev => ({
      ...prev,
      brand_color: '#FF9902',
      brand_color_light: '#ffb84d',
      brand_color_dark: '#e68a00',
      secondary_color: '#2E3192',
      secondary_color_light: '#4a4db8',
      secondary_color_dark: '#25287a',
      name: 'PEXIBLOCK',
      theme: 'dark',
      border_radius: '0.75rem',
      font_family: '"Alan Sans", sans-serif',
      use_custom_branding: false,
    }))
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      const profileData = {
        ...formData,
        country:
          typeof formData.country === 'string'
            ? parseInt(formData.country) || undefined
            : formData.country,
      }
      await updateProfile(profileData)
      setEditing(false)
    } catch (error) {
      console.error('Failed to update profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      company_name: user?.company_name || '',
      phone_number: user?.phone_number || '',
      address: user?.address || '',
      country: user?.country || '',
      // Branding fields
      currency: user?.currency || 'USD',
      language: user?.language || 'en',
      theme: user?.theme || 'dark',

      // Primary Brand Colors
      brand_color: user?.brand_color || '#FF9902',
      brand_color_light: user?.brand_color_light || '',
      brand_color_dark: user?.brand_color_dark || '',

      // Secondary Brand Colors
      secondary_color: user?.secondary_color || '#2E3192',
      secondary_color_light: user?.secondary_color_light || '',
      secondary_color_dark: user?.secondary_color_dark || '',

      // Brand Assets
      author_image_url: user?.author_image_url || '',
      image_url: user?.image_url || '',
      name: user?.name || 'PEXIBLOCK',

      // UI Customization
      border_radius: user?.border_radius || '0.75rem',
      font_family: user?.font_family || '"Alan Sans", sans-serif',

      // Branding Settings
      use_custom_branding: user?.use_custom_branding || false,
    })
    setEditing(false)
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'text-red-400 bg-red-900/20 border-red-500/30'
      case 'merchant':
        return 'text-blue-400 bg-blue-900/20 border-blue-500/30'
      case 'auditor':
        return 'text-purple-400 bg-purple-900/20 border-purple-500/30'
      default:
        return 'text-gray-400 bg-gray-800/50 border-gray-600'
    }
  }

  const getEnvironmentColor = (env: string) => {
    return env === 'production'
      ? 'text-emerald-400 bg-emerald-900/20 border-emerald-500/30'
      : 'text-amber-400 bg-amber-900/20 border-amber-500/30'
  }

  return (
    <div className="space-y-6" style={{ ...pageStyles.container, backgroundColor: currentBranding.backgroundColor, padding: '5px' }}>
      {/* Profile Header */}
      <div
        className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
        style={{ ...cardStyles, }}
      >
        <div className="py-8 card-content">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-6">
              <div className="relative">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={user?.first_name || ''} alt={user?.first_name || ''} />
                  <AvatarFallback className="text-2xl text-white bg-gradient-to-r from-orange-500 to-red-500">
                    {user?.first_name?.[0]}
                    {user?.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
              </div>
              <div>
                <h2 className="text-2xl font-bold" style={pageStyles.text}>
                  {user?.first_name} {user?.last_name}
                </h2>
                <p style={pageStyles.textSecondary}>{user?.email}</p>
                <div className="flex items-center mt-2 space-x-2">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getRoleColor(user?.role || '')}`}
                  >
                    {user?.role}
                  </span>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getEnvironmentColor(user?.environment || '')}`}
                  >
                    {user?.environment}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {editing ? (
                <>
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    disabled={loading}
                    style={getButtonStyles(currentBranding, 'outline')}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={loading}
                    style={getButtonStyles(currentBranding, 'primary')}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => setEditing(true)}
                  style={getButtonStyles(currentBranding, 'primary')}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Information */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Personal Information */}
        <div
          className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
          style={cardStyles}
        >
          <div className="card-header">
            <h3 className="text-lg card-title" style={pageStyles.text}>Personal Information</h3>
            <p className="card-description" style={pageStyles.textSecondary}>
              Your personal details and contact information
            </p>
          </div>
          <div className="space-y-4 card-content">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>First Name</label>
                <div className="relative">
                  <User className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2" style={{ color: pageStyles.textSecondary.color }} />
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={e => handleInputChange('first_name', e.target.value)}
                    disabled={!editing}
                    className={`input pl-10 ${!editing ? 'opacity-60' : ''}`}
                    style={inputStyles}
                  />
                </div>
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>Last Name</label>
                <div className="relative">
                  <User className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2" style={{ color: pageStyles.textSecondary.color }} />
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={e => handleInputChange('last_name', e.target.value)}
                    disabled={!editing}
                    className={`input pl-10 ${!editing ? 'opacity-60' : ''}`}
                    style={inputStyles}
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>Email Address</label>
              <div className="relative">
                <Mail className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2" style={{ color: pageStyles.textSecondary.color }} />
                <input
                  type="email"
                  value={formData.email}
                  disabled
                  className="pl-10 input opacity-60"
                  style={inputStyles}
                />
                <div className="absolute transform -translate-y-1/2 right-3 top-1/2">
                  <CheckCircle className="w-4 h-4" style={{ color: statusColors.success.color }} />
                </div>
              </div>
              <p className="mt-1 text-xs" style={pageStyles.textSecondary}>Email cannot be changed</p>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>Phone Number</label>
              <div className="relative">
                <Phone className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2" style={{ color: pageStyles.textSecondary.color }} />
                <input
                  type="tel"
                  value={formData.phone_number}
                  onChange={e => handleInputChange('phone_number', e.target.value)}
                  disabled={!editing}
                  className={`input pl-10 ${!editing ? 'opacity-60' : ''}`}
                  style={inputStyles}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Business Information */}
        <div
          className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
          style={cardStyles}
        >
          <div className="card-header">
            <h3 className="text-lg card-title" style={pageStyles.text}>Business Information</h3>
            <p className="card-description" style={pageStyles.textSecondary}>
              Your business details and company information
            </p>
          </div>
          <div className="space-y-4 card-content">
            <div>
              <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>Company Name</label>
              <div className="relative">
                <Building className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2" style={{ color: pageStyles.textSecondary.color }} />
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={e => handleInputChange('company_name', e.target.value)}
                  disabled={!editing}
                  className={`input pl-10 ${!editing ? 'opacity-60' : ''}`}
                  style={inputStyles}
                  placeholder="Your Company Name"
                />
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>Address</label>
              <div className="relative">
                <MapPin className="absolute w-4 h-4 left-3 top-3" style={{ color: pageStyles.textSecondary.color }} />
                <textarea
                  value={formData.address}
                  onChange={e => handleInputChange('address', e.target.value)}
                  disabled={!editing}
                  className={`input pl-10 ${!editing ? 'opacity-60' : ''}`}
                  style={inputStyles}
                  rows={3}
                  placeholder="Your business address"
                />
              </div>
            </div>

            <div className="z-[99999]">
              <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>Country</label>
              <div className="relative" ref={countryDropdownRef}>
                <div className="relative">
                  <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2" style={{ color: pageStyles.textSecondary.color }} />
                  <input
                    type="text"
                    value={countrySearchQuery}
                    onChange={e => handleCountrySearch(e.target.value)}
                    onFocus={() => setShowCountryDropdown(true)}
                    disabled={!editing}
                    placeholder="Search countries (e.g., United States, Canada, UK)"
                    className={`input pl-10 pr-10 ${!editing ? 'opacity-60' : ''}`}
                    style={inputStyles}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    disabled={!editing}
                    className="absolute transform -translate-y-1/2 right-3 top-1/2 hover:opacity-70 disabled:opacity-50"
                    style={{ color: pageStyles.textSecondary.color }}
                  >
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`}
                    />
                  </button>
                </div>

                {showCountryDropdown && editing && (
                  <div
                    className="absolute z-[99999] w-full mt-1 overflow-y-auto rounded-lg shadow-lg max-h-64"
                    style={{
                      backgroundColor: cardStyles.backgroundColor,
                      borderColor: cardStyles.borderColor,
                      border: '1px solid'
                    }}
                  >
                    {countrySearchQuery.length === 0 && popularCountries.length > 0 && (
                      <div className="p-3" style={{ borderBottomColor: cardStyles.borderColor, borderBottom: '1px solid' }}>
                        <div className="flex items-center space-x-2 text-sm font-medium" style={pageStyles.textSecondary}>
                          <Star className="w-4 h-4" style={{ color: pageStyles.primary.color }} />
                          <span>Popular Countries</span>
                        </div>
                      </div>
                    )}

                    {countrySearchQuery.length === 0 && popularCountries.length > 0 && (
                      <div className="overflow-y-auto max-h-32">
                        {popularCountries.map(country => (
                          <button
                            key={country.id}
                            type="button"
                            onClick={() => selectCountry(country)}
                            className="flex items-center justify-between w-full px-4 py-3 text-left hover:opacity-80"
                            style={{ backgroundColor: 'transparent' }}
                          >
                            <div>
                              <div className="font-medium" style={pageStyles.text}>{country.name}</div>
                              <div className="text-sm" style={pageStyles.textSecondary}>
                                {country.iso_code} • {country.dial_code}
                              </div>
                            </div>
                            <Star className="w-4 h-4" style={{ color: pageStyles.primary.color }} />
                          </button>
                        ))}
                      </div>
                    )}

                    {countrySearchQuery.length > 0 && (
                      <div className="p-3" style={{ borderBottomColor: cardStyles.borderColor, borderBottom: '1px solid' }}>
                        <div className="text-sm font-medium" style={pageStyles.textSecondary}>Search Results</div>
                      </div>
                    )}

                    <div className="overflow-y-auto max-h-32">
                      {countryLoading ? (
                        <div className="p-4 text-center">
                          <Loader2 className="w-5 h-5 mx-auto animate-spin" style={{ color: pageStyles.textSecondary.color }} />
                          <p className="mt-2 text-sm" style={pageStyles.textSecondary}>Searching countries...</p>
                        </div>
                      ) : countries.length > 0 ? (
                        countries.map(country => (
                          <button
                            key={country.id}
                            type="button"
                            onClick={() => selectCountry(country)}
                            className="flex items-center justify-between w-full px-4 py-3 text-left hover:opacity-80"
                            style={{ backgroundColor: 'transparent' }}
                          >
                            <div>
                              <div className="font-medium" style={pageStyles.text}>{country.name}</div>
                              <div className="text-sm" style={pageStyles.textSecondary}>
                                {country.iso_code} • {country.dial_code}
                              </div>
                            </div>
                          </button>
                        ))
                      ) : countrySearchQuery.length > 0 ? (
                        <div className="p-4 text-center" style={pageStyles.textSecondary}>
                          <p className="text-sm">No countries found for "{countrySearchQuery}"</p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Branding Information */}
      <div className="space-y-6">
        {/* Branding Preview Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold" style={pageStyles.text}>Branding & Customization</h2>
            <p style={pageStyles.textSecondary}>Customize your brand appearance and theme</p>
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setShowBrandingPreview(!showBrandingPreview)}
              style={getButtonStyles(currentBranding, 'outline')}
            >
              <Eye className="w-4 h-4 mr-2" />
              {showBrandingPreview ? 'Hide Preview' : 'Show Preview'}
            </Button>
            {isCustomBranding && (
              <Button
                variant="outline"
                onClick={handleResetBranding}
                style={getButtonStyles(currentBranding, 'outline')}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset to Default
              </Button>
            )}
          </div>
        </div>

        {/* Branding Preview */}
        {showBrandingPreview && (
          <BrandingPreview
            branding={createBrandingFromUser(formData)}
            onReset={handleResetBranding}
            showResetButton={isCustomBranding}
          />
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Brand Settings */}
          <div
            className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
            style={cardStyles}
          >
            <div className="card-header">
              <h3 className="text-lg card-title" style={pageStyles.text}>Brand Settings</h3>
              <p className="card-description" style={pageStyles.textSecondary}>
                Customize your brand appearance and settings
              </p>
            </div>
            <div className="space-y-4 card-content">
              <div>
                <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>Brand Name</label>
                <div className="relative">
                  <Building className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2" style={{ color: pageStyles.textSecondary.color }} />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => handleBrandingChange('name', e.target.value)}
                    disabled={!editing}
                    className={`input pl-10 ${!editing ? 'opacity-60' : ''}`}
                    style={inputStyles}
                    placeholder="Your Brand Name"
                  />
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>Primary Brand Color</label>
                <div className="flex items-center space-x-3">
                  <div className="relative flex-1">
                    <Palette className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2" style={{ color: pageStyles.textSecondary.color }} />
                    <input
                      type="text"
                      value={formData.brand_color}
                      onChange={e => handleBrandingChange('brand_color', e.target.value)}
                      disabled={!editing}
                      className={`input pl-10 ${!editing ? 'opacity-60' : ''}`}
                      style={inputStyles}
                      placeholder="#FF9902"
                    />
                  </div>
                  <div
                    className="w-10 h-10 rounded border"
                    style={{
                      backgroundColor: formData.brand_color,
                      borderColor: cardStyles.borderColor
                    }}
                  />
                </div>
                {formData.brand_color_light && formData.brand_color_dark && (
                  <div className="flex space-x-2 mt-2">
                    <div className="text-center">
                      <div
                        className="w-6 h-6 rounded border"
                        style={{
                          backgroundColor: formData.brand_color_light,
                          borderColor: cardStyles.borderColor
                        }}
                      />
                      <p className="text-xs" style={pageStyles.textSecondary}>Light</p>
                    </div>
                    <div className="text-center">
                      <div
                        className="w-6 h-6 rounded border"
                        style={{
                          backgroundColor: formData.brand_color_dark,
                          borderColor: cardStyles.borderColor
                        }}
                      />
                      <p className="text-xs" style={pageStyles.textSecondary}>Dark</p>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>Secondary Brand Color</label>
                <div className="flex items-center space-x-3">
                  <div className="relative flex-1">
                    <Palette className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2" style={{ color: pageStyles.textSecondary.color }} />
                    <input
                      type="text"
                      value={formData.secondary_color}
                      onChange={e => handleBrandingChange('secondary_color', e.target.value)}
                      disabled={!editing}
                      className={`input pl-10 ${!editing ? 'opacity-60' : ''}`}
                      style={inputStyles}
                      placeholder="#2E3192"
                    />
                  </div>
                  <div
                    className="w-10 h-10 rounded border"
                    style={{
                      backgroundColor: formData.secondary_color,
                      borderColor: cardStyles.borderColor
                    }}
                  />
                </div>
                {formData.secondary_color_light && formData.secondary_color_dark && (
                  <div className="flex space-x-2 mt-2">
                    <div className="text-center">
                      <div
                        className="w-6 h-6 rounded border"
                        style={{
                          backgroundColor: formData.secondary_color_light,
                          borderColor: cardStyles.borderColor
                        }}
                      />
                      <p className="text-xs" style={pageStyles.textSecondary}>Light</p>
                    </div>
                    <div className="text-center">
                      <div
                        className="w-6 h-6 rounded border"
                        style={{
                          backgroundColor: formData.secondary_color_dark,
                          borderColor: cardStyles.borderColor
                        }}
                      />
                      <p className="text-xs" style={pageStyles.textSecondary}>Dark</p>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>Theme</label>
                <div className="relative">
                  {formData.theme === 'dark' ? (
                    <Moon className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2" style={{ color: pageStyles.textSecondary.color }} />
                  ) : (
                    <Sun className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2" style={{ color: pageStyles.textSecondary.color }} />
                  )}
                  <select
                    value={formData.theme}
                    onChange={e => handleBrandingChange('theme', e.target.value)}
                    disabled={!editing}
                    className={`input pl-10 ${!editing ? 'opacity-60' : ''}`}
                    style={inputStyles}
                  >
                    <option value="dark">Dark</option>
                    <option value="light">Light</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>Border Radius</label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.border_radius}
                    onChange={e => handleBrandingChange('border_radius', e.target.value)}
                    disabled={!editing}
                    className={`input ${!editing ? 'opacity-60' : ''}`}
                    style={inputStyles}
                    placeholder="0.75rem"
                  />
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>Font Family</label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.font_family}
                    onChange={e => handleBrandingChange('font_family', e.target.value)}
                    disabled={!editing}
                    className={`input ${!editing ? 'opacity-60' : ''}`}
                    style={inputStyles}
                    placeholder='"Alan Sans", sans-serif'
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Localization Settings */}
          <div
            className="transition-all z-[2] duration-300 border card hover:shadow-xl backdrop-blur-sm"
            style={cardStyles}
          >
            <div className="card-header">
              <h3 className="text-lg card-title" style={pageStyles.text}>Localization</h3>
              <p className="card-description" style={pageStyles.textSecondary}>Language and currency preferences</p>
            </div>
            <div className="space-y-4 card-content">
              <div>
                <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>Currency</label>
                <div className="relative">
                  <DollarSign className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2" style={{ color: pageStyles.textSecondary.color }} />
                  <select
                    value={formData.currency}
                    onChange={e => handleInputChange('currency', e.target.value)}
                    disabled={!editing}
                    className={`input pl-10 ${!editing ? 'opacity-60' : ''}`}
                    style={inputStyles}
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                    <option value="AUD">AUD - Australian Dollar</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>Language</label>
                <div className="relative">
                  <Languages className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2" style={{ color: pageStyles.textSecondary.color }} />
                  <select
                    value={formData.language}
                    onChange={e => handleInputChange('language', e.target.value)}
                    disabled={!editing}
                    className={`input pl-10 ${!editing ? 'opacity-60' : ''}`}
                    style={inputStyles}
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Media Assets */}
      <div
        className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
        style={cardStyles}
      >
        <div className="card-header">
          <h3 className="text-lg card-title" style={pageStyles.text}>Media Assets</h3>
          <p className="card-description" style={pageStyles.textSecondary}>Upload your brand images and logos</p>
        </div>
        <div className="space-y-4 card-content">
          {/* Upload Error Display */}
          {uploadError && (
            <div
              className="p-3 text-sm rounded-lg"
              style={{
                color: statusColors.error.color,
                backgroundColor: statusColors.error.backgroundColor,
                borderColor: statusColors.error.borderColor,
                border: '1px solid'
              }}
            >
              {uploadError}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Profile Image Upload */}
            <div>
              <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>
                Author/Profile Image
              </label>
              <div className="space-y-3">
                {/* File Upload Button */}
                <div className="relative">
                  <input
                    type="file"
                    id="profile-image-upload"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={e => handleFileInputChange(e, 'profile')}
                    className="hidden"
                    disabled={!editing}
                  />
                  <label
                    htmlFor="profile-image-upload"
                    className={`flex items-center justify-center w-full p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${editing
                      ? 'hover:opacity-80'
                      : 'opacity-60 cursor-not-allowed'
                      }`}
                    style={{
                      borderColor: cardStyles.borderColor,
                      backgroundColor: editing ? 'transparent' : 'rgba(0,0,0,0.1)'
                    }}
                  >
                    {uploadingProfileImage ? (
                      <div className="flex items-center space-x-2" style={{ color: pageStyles.primary.color }}>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Uploading...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2" style={pageStyles.textSecondary}>
                        <Image className="w-4 h-4" />
                        <span className="text-sm">Choose Profile Image</span>
                      </div>
                    )}
                  </label>
                </div>

                {/* URL Input */}
                <div className="relative">
                  <Image className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2" style={{ color: pageStyles.textSecondary.color }} />
                  <input
                    type="url"
                    value={formData.author_image_url}
                    onChange={e => handleInputChange('author_image_url', e.target.value)}
                    disabled={!editing}
                    className={`input pl-10 ${!editing ? 'opacity-60' : ''}`}
                    style={inputStyles}
                    placeholder="Or enter image URL"
                  />
                </div>

                {/* Image Preview */}
                {formData.author_image_url && (
                  <div className="mt-2">
                    <img
                      src={formData.author_image_url}
                      alt="Author preview"
                      className="w-20 h-20 rounded object-cover border"
                      style={{ borderColor: cardStyles.borderColor }}
                      onError={e => {
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Brand Image Upload */}
            <div>
              <label className="block mb-2 text-sm font-medium" style={pageStyles.textSecondary}>
                Brand/Logo Image
              </label>
              <div className="space-y-3">
                {/* File Upload Button */}
                <div className="relative">
                  <input
                    type="file"
                    id="brand-image-upload"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={e => handleFileInputChange(e, 'brand')}
                    className="hidden"
                    disabled={!editing}
                  />
                  <label
                    htmlFor="brand-image-upload"
                    className={`flex items-center justify-center w-full p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${editing
                      ? 'hover:opacity-80'
                      : 'opacity-60 cursor-not-allowed'
                      }`}
                    style={{
                      borderColor: cardStyles.borderColor,
                      backgroundColor: editing ? 'transparent' : 'rgba(0,0,0,0.1)'
                    }}
                  >
                    {uploadingBrandImage ? (
                      <div className="flex items-center space-x-2" style={{ color: pageStyles.primary.color }}>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Uploading...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2" style={pageStyles.textSecondary}>
                        <Image className="w-4 h-4" />
                        <span className="text-sm">Choose Brand Image</span>
                      </div>
                    )}
                  </label>
                </div>

                {/* URL Input */}
                <div className="relative">
                  <Image className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2" style={{ color: pageStyles.textSecondary.color }} />
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={e => handleInputChange('image_url', e.target.value)}
                    disabled={!editing}
                    className={`input pl-10 ${!editing ? 'opacity-60' : ''}`}
                    style={inputStyles}
                    placeholder="Or enter image URL"
                  />
                </div>

                {/* Image Preview */}
                {formData.image_url && (
                  <div className="mt-2">
                    <img
                      src={formData.image_url}
                      alt="Brand preview"
                      className="w-20 h-20 rounded object-cover border"
                      style={{ borderColor: cardStyles.borderColor }}
                      onError={e => {
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account Status */}
      <div
        className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
        style={cardStyles}
      >
        <div className="card-header">
          <h3 className="text-lg card-title" style={pageStyles.text}>Account Status</h3>
          <p className="card-description" style={pageStyles.textSecondary}>
            Your account verification and approval status
          </p>
        </div>
        <div className="card-content">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="flex items-center space-x-3">
              <div
                className="p-2 rounded-full"
                style={{
                  backgroundColor: user?.is_verified ? statusColors.success.backgroundColor : statusColors.warning.backgroundColor
                }}
              >
                {user?.is_verified ? (
                  <CheckCircle className="w-5 h-5" style={{ color: statusColors.success.color }} />
                ) : (
                  <XCircle className="w-5 h-5" style={{ color: statusColors.warning.color }} />
                )}
              </div>
              <div>
                <p className="font-medium" style={pageStyles.text}>Email Verification</p>
                <p className="text-sm" style={pageStyles.textSecondary}>
                  {user?.is_verified ? 'Verified' : 'Pending verification'}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div
                className="p-2 rounded-full"
                style={{
                  backgroundColor: user?.is_account_approved ? statusColors.success.backgroundColor : statusColors.warning.backgroundColor
                }}
              >
                {user?.is_account_approved ? (
                  <CheckCircle className="w-5 h-5" style={{ color: statusColors.success.color }} />
                ) : (
                  <XCircle className="w-5 h-5" style={{ color: statusColors.warning.color }} />
                )}
              </div>
              <div>
                <p className="font-medium" style={pageStyles.text}>Account Approval</p>
                <p className="text-sm" style={pageStyles.textSecondary}>
                  {user?.is_account_approved ? 'Approved' : 'Pending approval'}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div
                className="p-2 rounded-full"
                style={{
                  backgroundColor: user?.is_verified && user?.is_account_approved ? statusColors.success.backgroundColor : statusColors.warning.backgroundColor
                }}
              >
                {user?.is_verified && user?.is_account_approved ? (
                  <CheckCircle className="w-5 h-5" style={{ color: statusColors.success.color }} />
                ) : (
                  <XCircle className="w-5 h-5" style={{ color: statusColors.warning.color }} />
                )}
              </div>
              <div>
                <p className="font-medium" style={pageStyles.text}>Production Ready</p>
                <p className="text-sm" style={pageStyles.textSecondary}>
                  {user?.is_verified && user?.is_account_approved ? 'Ready' : 'Not ready'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
